/**
 * "klient od 14 mies." — the one line under the client's status in the summary column's identity
 * block (KANON view 04).
 *
 * Ported from mobile's `features/clients/model/clientSince.ts` rather than re-derived, because the
 * two apps print the same sentence about the same client and a trainer switching between them must
 * not read two different numbers. The test vectors below are that file's, and they are the reason
 * the arithmetic is not "days / 30".
 *
 * ── Read strictly in UTC, and that is load-bearing ─────────────────────────────────────────────
 * `sinceDate` is a UTC instant. Reading its day in the browser's zone shifts the anchor into the
 * neighbouring day for every trainer east or west of the meridian, which flips `klient od 1 dnia`
 * to `klient od 2 dni` depending on where they opened the app.
 *
 * ── Why the wording is decided here and not by the backend ─────────────────────────────────────
 * The contract ships the anchor date alone. It used to ship pre-rendered Polish, which broke the
 * English UI; the sentence belongs to whoever knows the reader's locale, which is the client.
 */

/**
 * Full calendar months between two instants, in UTC, floored at zero.
 *
 * Calendar months, not 30-day blocks: a client taken on 20 June is not "1 mies." on 19 July, and
 * mobile's own vector for that case (`2026-06-20` at `2026-07-16` → `klient od 26 dni`) is what
 * keeps this honest.
 */
function fullMonthsBetween(from: Date, to: Date): number {
  let months =
    (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth());
  if (to.getUTCDate() < from.getUTCDate()) months -= 1;
  return Math.max(0, months);
}

const MS_PER_DAY = 86_400_000;

/**
 * @param sinceDate the coaching relationship's start, as the contract's UTC instant string
 * @param t the translator, passed in — `model/` never imports i18next
 * @param now injected so the tests can stand still
 * @returns the sentence, or `undefined` when there is no usable anchor and the caller should print
 *   the status alone rather than a placeholder
 */
export function clientSinceLabel(
  sinceDate: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  now: Date = new Date(),
): string | undefined {
  if (sinceDate == null || sinceDate === '') return undefined;
  const anchor = new Date(sinceDate);
  if (Number.isNaN(anchor.getTime())) return undefined;

  const elapsedDays = (now.getTime() - anchor.getTime()) / MS_PER_DAY;
  if (elapsedDays < 1) return t('web:clientDetail.since.today');

  const months = fullMonthsBetween(anchor, now);
  if (months >= 1) return t('web:clientDetail.since.months', { count: months });

  return t('web:clientDetail.since.days', { count: Math.floor(elapsedDays) });
}
